import React, { useEffect, useState, useMemo } from "react";
import { ColumnFilterRow, applyColumnFilters } from "../../../Components/Common/DashboardKit";
import useTableSort from "../../../Components/Common/DashboardKit/useTableSort";
import SortableTh from "../../../Components/Common/DashboardKit/SortableTh";
import exportRowsToCsv from "../../../Components/Common/DashboardKit/exportCsv";
import { Card, CardBody, CardHeader, Container, Table, Button, Badge, Spinner, Alert } from "reactstrap";
import { useNavigate } from "react-router-dom";
import api from "../../../api";
import { DashboardPageHeader, TableSkeleton } from "../../../Components/Common/DashboardKit";

/**
 * Round R12 (2026) — My Drafts inbox page.
 *
 * Surfaces drafts (Status=60) that the current user authored. Uses the
 * existing GET /api/dms/drafts?authorId=N endpoint (already wired Phase 2.1).
 *
 * Each row → click → resumes editing at /dms/edit/<doctype-slug>/<dmsId>.
 *
 * Note about Status=60 (Q11 from the Phase-1 audit):
 *   • The DB has Status=60 reserved as the modern-only "Draft" state.
 *   • Legacy never used 60 — legacy drafts went straight to Status=0 with
 *     a placeholder DocID. Modern moved drafts to a dedicated state so
 *     they don't appear in approval queues.
 *   • Submission converts 60 → 0 and burns a real DocID via the
 *     POST /api/dms/ecn/submit/{dmsId} endpoint.
 */

const DOCTYPE_SLUG = { 1: "sop", 2: "wi", 3: "fcd", 4: "ecn", 5: "lit" };
const DOCTYPE_LABEL = { 1: "SOP", 2: "WI", 3: "FCD", 4: "ECN", 5: "LIT" };

const MyDrafts = () => {
    const navigate = useNavigate();
    const callerEmpId = useMemo(() => {
        try {
            const u = JSON.parse(sessionStorage.getItem("authUser") || "{}");
            return Number(u.uid) || 0;
        } catch { return 0; }
    }, []);

    const [drafts, setDrafts] = useState([]);
    // Round 34 — EPO-style per-column filters + click-to-sort.
    const [columnFilters, setColumnFilters] = useState({});
    const draftColumns = React.useMemo(() => [
        { id: "type",      label: "Type",       type: "text", get: (d) => DOCTYPE_LABEL[d.docType] || "Doc" },
        { id: "docId",     label: "Doc ID",     type: "text", get: (d) => d.docId || "(unsaved)" },
        { id: "docTitle",  label: "Title",      type: "text", get: (d) => d.docTitle || "" },
        { id: "rev",       label: "Rev",        type: "text", get: (d) => String(d.rev ?? 0) },
        { id: "createdTime", label: "Last Saved", type: "date", get: (d) => d.createdTime || "" },
        { id: "act",       label: "",           type: "none" },
    ], []);
    const { sorted: draftView, sort, toggleSort } = useTableSort(
        applyColumnFilters(drafts, draftColumns, columnFilters), draftColumns
    );
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            setError(null);
            try {
                const res = await api.get("/dms/drafts", { params: { authorId: callerEmpId } });
                setDrafts(res.data || []);
            } catch (err) {
                setError(err?.response?.data?.error || err?.message || "Failed to load drafts.");
            } finally {
                setLoading(false);
            }
        };
        if (callerEmpId > 0) load();
    }, [callerEmpId]);

    const openDraft = (d) => {
        const slug = DOCTYPE_SLUG[d.docType] || "ecn";
        navigate(`/dms/edit/${slug}/${d.dmsId}`);
    };

    // Hotfix 18 — delete a draft. Confirms, calls the guarded
    // DELETE /api/dms/draft/{dmsId} endpoint, then removes the row from
    // local state on success (no full refetch needed).
    const [deletingId, setDeletingId] = useState(null);
    const deleteDraft = async (d) => {
        const label = `${DOCTYPE_LABEL[d.docType] || "Doc"} "${d.docTitle || d.docId}"`;
        if (!window.confirm(`Delete draft ${label}? This cannot be undone.`)) return;
        setDeletingId(d.dmsId);
        try {
            await api.delete(`/dms/draft/${d.dmsId}`);
            setDrafts((prev) => prev.filter((x) => x.dmsId !== d.dmsId));
        } catch (err) {
            const data = err?.response?.data;
            const msg = typeof data === "string" ? data
                : data?.title || data?.error || err?.message || "Failed to delete draft.";
            window.alert(`Could not delete draft:\n${msg}`);
        } finally {
            setDeletingId(null);
        }
    };

    return (
        <div className="page-content">
            <Container fluid>
                <DashboardPageHeader
                    icon="ri-draft-line"
                    title="My Drafts"
                    subtitle="Documents you've saved but not yet submitted for approval"
                    tone="violet"
                    breadcrumb={[
                        { label: "Dashboards", to: "/" },
                        { label: "DMS", to: "/workflows?wf=dms" },
                        { label: "My Drafts" },
                    ]}
                />{/* Hotfix 10 — DMS breadcrumb segment navigates back to the doc list */}

                <Card>
                    <CardHeader className="d-flex align-items-center">
                        <div className="flex-grow-1">
                            <h5 className="card-title mb-0">
                                <i className="ri-draft-line align-bottom me-1 text-primary"></i>
                                My Drafts
                                {!loading && drafts.length > 0 && (
                                    <Badge color="light" className="ms-2 text-dark">{drafts.length}</Badge>
                                )}
                            </h5>
                            <p className="text-muted small mb-0 mt-1">
                                Documents you've saved but not yet submitted for approval. Click to resume editing.
                            </p>
                        </div>
                        {/* Hotfix 12 — removed the "+ New ECN" button.
                            It hard-coded a single doctype (ECN), but
                            drafts span all doctypes (ECN / WI / SOP /
                            FCD / LIT). The proper way to start a new
                            doc is the "Create new" button on
                            /document-management, which shows a
                            doctype picker covering every supported
                            type. Keeping two creation paths with
                            different scopes was confusing. */}
                    </CardHeader>
                    <CardBody>
                        {loading && (
                            <div className="table-responsive">
                                <Table className="align-middle mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            <th>Type</th><th>Doc ID</th><th>Title</th>
                                            <th>Rev</th><th>Last Saved</th><th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <TableSkeleton rows={6} columns={6} />
                                    </tbody>
                                </Table>
                            </div>
                        )}

                        {!loading && error && (
                            <Alert color="danger">
                                <i className="ri-error-warning-line align-bottom me-1"></i>
                                {error}
                            </Alert>
                        )}

                        {!loading && !error && drafts.length === 0 && (
                            <div className="text-center py-5 text-muted">
                                <i className="ri-inbox-line" style={{ fontSize: 36 }}></i>
                                <div className="mt-2">No drafts yet.</div>
                                <div className="small mt-1">
                                    Start a new document and click "Save Draft" to keep working on it later.
                                </div>
                            </div>
                        )}

                        {!loading && !error && drafts.length > 0 && (
                            <>
                                                        {/* Round 29 — CSV of the full list */}
                            <div className="d-flex justify-content-end mb-2">
                                <button type="button" className="btn btn-soft-primary btn-sm" onClick={() => exportRowsToCsv("dms-my-drafts.csv", ((r0) => Object.keys(r0 || {}).map((k) => ({ key: k, label: k })))(drafts[0]), drafts)}>
                                    <i className="ri-download-2-line align-bottom me-1" />Export CSV ({drafts.length})
                                </button>
                            </div>
<div className="table-responsive">
                                <Table hover className="align-middle mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            {draftColumns.slice(0, 5).map((c) => (
                                                <SortableTh key={c.id} col={c} sort={sort} onToggle={toggleSort} />
                                            ))}
                                            <th></th>
                                        </tr>
                                        <ColumnFilterRow columns={draftColumns} filters={columnFilters} onChange={setColumnFilters} />
                                    </thead>
                                    <tbody>
                                        {draftView.map(d => (
                                            <tr
                                                key={d.dmsId}
                                                style={{ cursor: "pointer" }}
                                                onClick={() => openDraft(d)}
                                            >
                                                <td>
                                                    <Badge color="info" pill>
                                                        {DOCTYPE_LABEL[d.docType] || "Doc"}
                                                    </Badge>
                                                </td>
                                                <td><code>{d.docId || `(unsaved)`}</code></td>
                                                <td>{d.docTitle || <span className="text-muted">Untitled</span>}</td>
                                                <td>{d.rev ?? 0}</td>
                                                <td className="text-muted small">
                                                    {d.createdTime
                                                        ? new Date(d.createdTime).toLocaleString()
                                                        : "—"}
                                                </td>
                                                <td className="text-end">
                                                    <Button
                                                        size="sm"
                                                        color="primary"
                                                        outline
                                                        onClick={(e) => { e.stopPropagation(); openDraft(d); }}
                                                    >
                                                        <i className="ri-pencil-line align-bottom me-1"></i>
                                                        Resume
                                                    </Button>
                                                    {/* Hotfix 18 — delete draft */}
                                                    <Button
                                                        size="sm"
                                                        color="danger"
                                                        outline
                                                        className="ms-2"
                                                        disabled={deletingId === d.dmsId}
                                                        onClick={(e) => { e.stopPropagation(); deleteDraft(d); }}
                                                    >
                                                        {deletingId === d.dmsId ? (
                                                            <Spinner size="sm" />
                                                        ) : (
                                                            <>
                                                                <i className="ri-delete-bin-line align-bottom me-1"></i>
                                                                Delete
                                                            </>
                                                        )}
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                            </>
                        )}
                    </CardBody>
                </Card>
            </Container>
        </div>
    );
};

export default MyDrafts;
